import { nanoid } from 'nanoid';
import type { AdminStepScorm, ContentStep, ContentStepType } from '@/entities/Courses';
import type { IStreamVideo } from '@/shared/types/common';

export interface SessionStepDraft {
  clientId: string;
  serverId?: number;
  // Server state the draft was loaded from (or last saved to); used to send only changed fields
  existing?: ContentStep;
  videoUploaded?: boolean;
  scormUploaded?: boolean;
  expanded: boolean;
  title: string;
  type: ContentStepType | '';
  description: string;
  content: string;
  attachments: File[];
  removedAttachmentKeys: string[];
  videoFile: File | null;
  scormFile: File | null;
  // The saved video / SCORM package was deleted; hides it until the lesson is refetched
  removeVideo: boolean;
  removeScorm: boolean;
  scormGraded: boolean;
  scormPassingScore: number;
}

export const SESSION_STEP_LIMIT = 20;

export const DEFAULT_SCORM_PASSING_SCORE = 70;

// Limits of the step endpoints, see doc/FRONTEND_LESSON_STEPS.md
export const STEP_CONTENT_MAX_LENGTH = 200_000;
// The package goes straight to the storage in parts; the backend rejects a bigger fileSize with 400
export const SCORM_MAX_SIZE = 5 * 1024 * 1024 * 1024;
// Cloudflare Stream accepts up to 30 GB per video; the backend rejects a bigger fileSize with 400
export const STEP_VIDEO_MAX_SIZE = 30 * 1024 * 1024 * 1024;

const PASSING_SCORE_OPTIONS = Array.from({ length: 20 }, (_, index) => {
  const score = (index + 1) * 5;
  return { key: String(score), label: `${score}%` };
});

// Keeps a saved score that is not a multiple of 5 selectable
export const getPassingScoreOptions = (current: number) =>
  PASSING_SCORE_OPTIONS.some((option) => option.key === String(current))
    ? PASSING_SCORE_OPTIONS
    : [...PASSING_SCORE_OPTIONS, { key: String(current), label: `${current}%` }].sort(
      (a, b) => Number(a.key) - Number(b.key),
    );

export const createSessionStep = (): SessionStepDraft => ({
  clientId: nanoid(),
  expanded: true,
  title: '',
  type: '',
  description: '',
  content: '',
  attachments: [],
  removedAttachmentKeys: [],
  videoFile: null,
  scormFile: null,
  removeVideo: false,
  removeScorm: false,
  scormGraded: false,
  scormPassingScore: DEFAULT_SCORM_PASSING_SCORE,
});

/**
 * Edit mode: every change of a step is sent to the server right away.
 * Without these actions the steps are drafts saved together with the session.
 */
export interface StepLiveActions {
  addStep: () => Promise<void>;
  // Sends title / description / content / type that differ from the saved step
  saveFields: (step: SessionStepDraft, overrides?: Partial<SessionStepDraft>) => Promise<void>;
  uploadAttachments: (step: SessionStepDraft, files: File[]) => Promise<void>;
  removeAttachment: (step: SessionStepDraft, key: string) => Promise<void>;
  deleteStep: (step: SessionStepDraft) => Promise<boolean>;
  reorder: (next: SessionStepDraft[], previous: SessionStepDraft[]) => Promise<void>;
  saveScormSettings: (step: SessionStepDraft, graded: boolean, score: number) => Promise<void>;
  removeVideo: (step: SessionStepDraft) => Promise<void>;
  removeScorm: (step: SessionStepDraft) => Promise<void>;
  // Video uploads live in the uploads manager (features/StepVideoUploads): the modal can be
  // closed while they run, and the card picks the upload up again by its step id.
  // currentVideo is the step's video as the card shows it: the same file continues its
  // interrupted upload from where it stopped, a different file replaces the video
  startVideoUpload: (
    step: SessionStepDraft,
    stepNumber: number,
    file: File,
    currentVideo?: IStreamVideo | null,
  ) => void;
  // Cancels a running upload or removes a failed one together with the step's video placeholder
  cancelVideoUpload: (step: SessionStepDraft) => void;
  retryVideoUpload: (step: SessionStepDraft) => void;
  // SCORM packages go to their uploads manager too (features/StepScormUploads): the .zip is sent
  // straight to the storage in parts. currentScorm is the step's package as the card shows it:
  // the same file continues its interrupted upload, a different file replaces the package
  startScormUpload: (
    step: SessionStepDraft,
    stepNumber: number,
    file: File,
    currentScorm?: AdminStepScorm | null,
  ) => void;
  // Cancels a running upload or removes a failed one together with the step's package placeholder
  cancelScormUpload: (step: SessionStepDraft) => void;
  retryScormUpload: (step: SessionStepDraft) => void;
  isAdding: boolean;
  isStepSaving: (clientId: string) => boolean;
}

export const emptyContentStep = (id: number, order: number): ContentStep => ({
  id,
  order,
  type: 'general',
  title: null,
  description: null,
  content: null,
  attachments: [],
  video: null,
  scorm: null,
});

export const stepDraftFromContentStep =(step: ContentStep): SessionStepDraft => ({
  ...createSessionStep(),
  serverId: step.id,
  existing: step,
  expanded: false,
  title: step.title ?? '',
  type: step.type,
  description: step.description ?? '',
  content: step.content ?? '',
  scormGraded: step.scorm?.hasAssessment ?? false,
  scormPassingScore: step.scorm?.passingScore ?? DEFAULT_SCORM_PASSING_SCORE,
});

export const STEP_TYPE_OPTIONS: { key: ContentStepType; label: string }[] = [
  { key: 'general', label: 'General' },
  { key: 'video', label: 'Video' },
  { key: 'scorm', label: 'SCORM' },
];
